import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * SwarmChat — Floating chat widget that broadcasts ONE message to ALL of the
 * owner's agents at once. Each agent replies with their own perspective so
 * the owner gets multiple takes on a single issue.
 *
 * Why this exists: owner asked for "a chat widget that all my agents and
 * everyone and everything on my diagram is linked to so I can talk to them
 * all at once". Powered by POST /api/jarvis/swarm_chat.
 */
export default function SwarmChat({ open, onClose, autoMessage }) {
  const [thread, setThread] = useState([]); // [{role:'user'|'agent', name?, avatar?, text}]
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // CommandRail is fixed at left:0 with width 288px (w-72) when expanded.
  // Both SwarmChat and AgentsRoster sit on the right of it — clamp x so
  // they can never overlap the rail (owner complaint Feb 2026).
  const RAIL_RIGHT_EDGE = 300;
  const [pos, setPos] = useState({ x: RAIL_RIGHT_EDGE, y: 60 });
  const dragRef = useRef({ active: false, dx: 0, dy: 0 });
  const scrollRef = useRef(null);
  const sendRef = useRef(null);
  const debateRef = useRef(null);
  const conclaveRef = useRef(null);
  // Which mode was last active — swaps the header subtitle so the owner
  // knows what they're looking at (parallel DEBATE vs sequential HUDDLE).
  const [mode, setMode] = useState("ask");  // "ask" | "debate" | "conclave"
  const saveTimerRef = useRef(null);

  // Persist drag position so it stays where the owner put it
  useEffect(() => {
    try {
      const saved = localStorage.getItem("rawco-swarm-pos");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Clamp restored x so an old saved position (pre-fix) can't
        // park SwarmChat under the CommandRail.
        if (parsed && typeof parsed.x === "number") {
          parsed.x = Math.max(RAIL_RIGHT_EDGE, parsed.x);
        }
        setPos(parsed);
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem("rawco-swarm-pos", JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos]);
  const onDragStart = (e) => {
    dragRef.current = { active: true, dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      setPos({
        x: Math.max(RAIL_RIGHT_EDGE, Math.min(window.innerWidth - 380, e.clientX - dragRef.current.dx)),
        y: Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragRef.current.dy)),
      });
    };
    const onUp = () => { dragRef.current.active = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  // Load persisted thread from MongoDB the first time the panel opens, so the
  // owner sees the same conversation on every device / browser profile.
  useEffect(() => {
    if (!open || historyLoaded) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/jarvis/swarm_chat/history");
        if (!cancelled && Array.isArray(r.data?.thread)) {
          setThread(r.data.thread);
        }
        // ✅ Only mark history-loaded on SUCCESS. Owner brief (Feb 2026):
        // if the load failed (network blip) and we set historyLoaded=true
        // anyway, the save effect below would then POST our EMPTY local
        // thread back to the server — wiping the owner's swarm chat
        // history. Leaving historyLoaded=false on failure means the next
        // open retries the load instead of overwriting.
        if (!cancelled) setHistoryLoaded(true);
      } catch (err) {
        // Don't set historyLoaded — leave thread alone, retry next open
        if (!cancelled) console.warn("[SwarmChat] history load failed, will retry:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, historyLoaded]);

  // Debounced save: after every thread change push to Mongo 600ms later.
  // BUT only when we have something to save OR we previously loaded a
  // non-empty thread — never overwrite the server with a blank thread
  // unless the owner just clicked CLEAR HISTORY (which uses DELETE, not
  // POST). Otherwise on first mount with no messages we'd overwrite a
  // prior non-empty thread on another device.
  useEffect(() => {
    if (!historyLoaded) return undefined;
    if (thread.length === 0) return undefined; // see comment above
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      api.post("/jarvis/swarm_chat/history", { thread }).catch(() => {});
    }, 600);
    return () => clearTimeout(saveTimerRef.current);
  }, [thread, historyLoaded]);

  const clearHistory = async () => {
    if (busy) return;
    if (!window.confirm("Clear the entire SwarmChat history? This can't be undone.")) return;
    setThread([]);
    try { await api.delete("/jarvis/swarm_chat/history"); } catch { /* ignore */ }
  };

  // Listen for external "append to thread" events fired by App.js when the
  // brain emits a `talk_to_agent` action. Owner asked: "I asked Tom why my
  // screen keeps freezing but he didn't come up in the swarm chat last
  // time." We push the question and the agent's reply into the visible
  // thread so the exchange is there to scroll back through.
  useEffect(() => {
    const onAppend = (e) => {
      const d = (e && e.detail) || {};
      const agentName = (d.agentName || "Agent").toString();
      const agentReply = (d.agentReply || "").toString();
      const userMessage = (d.userMessage || "").toString();
      setThread((t) => {
        const next = [...t];
        if (userMessage) next.push({ role: "user", text: userMessage });
        if (agentReply) next.push({
          role: "agent",
          name: agentName,
          avatar: agentName.slice(0, 1).toUpperCase(),
          text: agentReply,
          ok: true,
        });
        return next;
      });
    };
    window.addEventListener("rawco-swarm-append", onAppend);
    return () => window.removeEventListener("rawco-swarm-append", onAppend);
  }, []);

  // Voice-routed: when "ask all my agents <X>" fires, autoMessage carries X and
  // we kick off the swarm immediately. If autoMessage starts with __DEBATE__:
  // we auto-trigger the 🎭 DEBATE flow instead.
  // 🚨 DEDUP — guard against the same autoMessage being re-applied on every
  // parent re-render. Owner saw Sarah repeating the SAME reply 6 times
  // because the effect was firing send() on every render where the parent
  // value re-emitted identically. We now skip if the message already fired.
  const lastFiredRef = useRef("");
  useEffect(() => {
    if (open && autoMessage && autoMessage.trim()) {
      const key = autoMessage.trim();
      if (lastFiredRef.current === key) return undefined;
      lastFiredRef.current = key;
      if (autoMessage.startsWith("__DEBATE__:")) {
        const topic = autoMessage.slice("__DEBATE__:".length);
        setInput(topic);
        const t = setTimeout(() => debateRef.current?.(), 100);
        return () => clearTimeout(t);
      }
      if (autoMessage.startsWith("__CONCLAVE__:")) {
        const topic = autoMessage.slice("__CONCLAVE__:".length);
        setInput(topic);
        // Fire the HUDDLE handler after a tiny beat so setInput lands first
        const t = setTimeout(() => conclaveRef.current?.(), 100);
        return () => clearTimeout(t);
      }
      setInput(autoMessage);
      const t = setTimeout(() => sendRef.current?.(), 80);
      return () => clearTimeout(t);
    }
    // Reset the dedup when the parent clears autoMessage (e.g. on close)
    if (!autoMessage) lastFiredRef.current = "";
    return undefined;
  }, [open, autoMessage]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    setMode("ask");
    setThread(t => [...t, { role: "user", text: msg }]);
    setBusy(true);
    try {
      // 🎯 SINGLE-AGENT ROUTING — owner complaint: "all my agents are
      // replying at once in chat when only ONE should be answering. If
      // it's a job for Dobby, only Dobby should reply." Detect a leading
      // "<agent>, ..." / "hey <agent> ..." / "ask <agent> ..." pattern
      // and route to /jarvis/chat (which will fire talk_to_agent for one
      // agent) instead of the full /swarm_chat fanout.
      const heyMatch = msg.match(
        /^\s*(?:hey|hi|hello|yo|ok|alright|ask|tell|message)?\s*([A-Za-z]+)[,:\s]+(.+)/i,
      );
      const knownAgents = ["tom","sarah","eva","scout","dobby"];
      if (heyMatch && knownAgents.includes(heyMatch[1].toLowerCase())) {
        const agentName = heyMatch[1];
        const inner = heyMatch[2];
        const r1 = await api.post("/jarvis/chat", {
          message: `hey ${agentName}, ${inner}`,
          session_id: localStorage.getItem("rawco_session_id") || "",
        });
        const reply = r1.data?.action?.result?.reply || r1.data?.reply || "";
        const voiceId = r1.data?.action?.result?.voice_id || "";
        if (reply) {
          setThread(t => [...t, { role: "agent", name: agentName, text: reply, voice_id: voiceId, ok: true }]);
        }
        setBusy(false);
        setInput("");
        return;
      }
      const r = await api.post("/jarvis/swarm_chat", { message: msg });
      const responses = r.data?.responses || [];
      const action = r.data?.proposed_action || null;
      const executed = r.data?.executed || null;
      const summary = r.data?.spoken_summary || "";

      // 🚀 AUTO-EXECUTED ACTIONS — backend now runs the action server-side
      // and returns {executed: {action: {type, result, ...}}}. We push it
      // through the SAME pipeline as the main brain (window dispatch →
      // App.js handleAction → BroadcastChannel → mirror). This is what
      // makes the card actually OPEN on the mirror screen instead of
      // sitting silently in chat. Owner complaint: "no widget pops up
      // when I ask anything".
      if (executed && executed.ok && executed.action) {
        try {
          window.dispatchEvent(new CustomEvent("rawco-swarm-action-execute", {
            detail: { action: executed.action, reply: executed.message || "" },
          }));
        } catch { /* ignore */ }
      }

      setThread(t => [...t,
        ...responses.map(resp => ({
          role: "agent", name: resp.name, avatar: resp.avatar,
          voice_id: resp.voice_id,
          text: resp.reply, ok: resp.ok,
        })),
        // If the backend AUTO-EXECUTED, show a confirmation pill instead
        // of the YES/FIX button — owner: "they should just do it".
        ...(executed
            ? [{ role: "action", action: { ...action, executed: true,
                                           result_message: executed.message || "Done." } }]
            : (action ? [{ role: "action", action }] : [])),
        ...(summary ? [{ role: "summary", text: summary }] : []),
      ]);
      // 🎙 TAKE-TURNS SPEAKING — owner asked: "the agents don't take
      // turns when chatting, they all talk over each other. I want them
      // to reply back to each other in communication that makes sense."
      // We now play each agent's response sequentially in their OWN
      // ElevenLabs voice, awaiting onended before moving to the next.
      // Jarvis's consolidated summary plays LAST.
      const playSequentially = async (queue) => {
        for (const item of queue) {
          if (!item?.text) continue;
          try {
            const tk = localStorage.getItem("rawco_token");
            const tts = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/tts`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
              body: JSON.stringify({ text: item.text, voice_id: item.voice_id || "" }),
            });
            if (!tts.ok) continue;
            const data = await tts.json();
            const audioUrl = data.audio ? `data:audio/mpeg;base64,${data.audio}` : null;
            if (!audioUrl) continue;
            const audio = new Audio(audioUrl);
            // Tell App.js which agent face to mount during playback.
            try {
              window.dispatchEvent(new CustomEvent("rawco-agent-face-show", {
                detail: { name: item.name || "Agent", voiceId: item.voice_id || "" },
              }));
              window.dispatchEvent(new CustomEvent("rawco-agent-face-audio", { detail: { audio } }));
            } catch { /* ignore */ }
            await new Promise((resolve) => {
              audio.onended = () => resolve();
              audio.onerror = () => resolve();
              audio.play().catch(() => resolve());
            });
            try { window.dispatchEvent(new Event("rawco-agent-face-hide")); } catch { /* ignore */ }
          } catch { /* skip this one, move on */ }
        }
      };
      // Build the queue: every agent in turn (with their own voice_id),
      // then Jarvis's spoken_summary at the end if present.
      const speakQueue = [
        ...responses.map(r => ({ name: r.name, text: r.reply, voice_id: r.voice_id })),
        ...(summary ? [{ name: "Raw Company", text: summary, voice_id: "" }] : []),
      ];
      // Fire-and-forget so the UI doesn't block on the TTS chain. The
      // global lock prevents useJarvis.speak() from cutting agents off
      // mid-sentence (root cause of "agents fighting, never finishing").
      (async () => {
        try { window.__rcoSwarmVoiceBusy = true; } catch {}
        try { await playSequentially(speakQueue); }
        finally {
          try { window.__rcoSwarmVoiceBusy = false; } catch {}
        }
      })();
    } catch (e) {
      setThread(t => [...t, { role: "agent", name: "system", avatar: "!",
        text: `Swarm failed: ${e.response?.data?.detail || e.message}`, ok: false }]);
    } finally { setBusy(false); }
  };

  const runAction = async (act, idx) => {
    setBusy(true);
    try {
      const r = await api.post("/jarvis/swarm_execute", { action_key: act.key });
      setThread(t => t.map((m, i) => i === idx ? {
        ...m,
        action: { ...m.action, executed: true, result_message: r.data?.message || "Done." },
      } : m));
    } catch (e) {
      setThread(t => t.map((m, i) => i === idx ? {
        ...m,
        action: { ...m.action, executed: true, failed: true, result_message: e.response?.data?.detail || e.message },
      } : m));
    } finally { setBusy(false); }
  };

  // 🔊 Per-agent voice playback — calls /api/jarvis/tts with the agent's
  // dedicated voice_id so each persona sounds like itself.
  const speakAgent = async (text, voiceId, agentName) => {
    try {
      // 🎭 Pop the agent's 3D face out while they speak (when user clicks
      // 🔊 next to an agent message in SwarmChat).
      try {
        if (agentName) {
          window.dispatchEvent(new CustomEvent("rawco-agent-face-show", {
            detail: { name: agentName, voiceId },
          }));
        }
      } catch { /* ignore */ }
      const tk = localStorage.getItem("rawco_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/tts`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ text, voice_id: voiceId }),
      });
      if (!r.ok) return;
      const blob = await r.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audio.onended = () => {
        try { window.dispatchEvent(new Event("rawco-agent-face-hide")); } catch {}
      };
      audio.play().catch(() => {});
      // Also pipe the audio element to the face stage so the mouth syncs
      try { window.dispatchEvent(new CustomEvent("rawco-agent-face-audio", { detail: { audio } })); } catch {}
    } catch { /* ignore */ }
  };

  // 🤝 CONCLAVE — sequential, name-referencing collaboration. Unlike
  // DEBATE (which fans out in parallel), Conclave has agents speak one
  // after another, each referencing prior agents by name and building
  // toward a joint plan. Owner Feb 2026: "Can my agents talk to each
  // other and collaborate by calling each other by their own names?"
  const startConclave = async () => {
    const msg = input.trim() || thread.findLast?.(m => m.role === "user")?.text || "Diagnose the biggest risk we're facing right now and propose one fix.";
    setBusy(true);
    setMode("conclave");
    setThread(t => [...t, { role: "user", text: `🤝 HUDDLE: ${msg}` }]);
    setInput("");
    try {
      const r = await api.post("/agents/conclave", { problem: msg, max_turns: 4 });
      const turns = r.data?.turns || [];
      // Post each turn as a distinct agent message so the UI matches
      // the DEBATE look, PLUS speak them in-voice sequentially so the
      // owner hears the huddle unfold.
      let voiceQueue = Promise.resolve();
      for (const t of turns) {
        setThread(prev => [...prev, {
          role: "agent",
          name: `${t.agent_name} · Turn ${t.turn}`,
          avatar: t.avatar,
          voice_id: t.voice_id,
          text: t.reply,
          ok: true,
        }]);
        if (t.voice_id) {
          voiceQueue = voiceQueue.then(() => speakAgent(t.reply, t.voice_id, t.agent_name));
        }
      }
      // Jarvis's synthesized 2-line recap
      if (r.data?.plan) {
        setThread(prev => [...prev, {
          role: "jarvis",
          name: "JARVIS",
          text: r.data.plan,
          ok: true,
        }]);
      }
      await voiceQueue;
    } catch (e) {
      setThread(prev => [...prev, { role: "system", text: `Conclave failed: ${e?.message || "unknown"}`, ok: false }]);
    } finally {
      setBusy(false);
    }
  };

  // 🎭 Agent-to-agent DEBATE — STREAMS each agent's reply as it lands via
  // Server-Sent Events. Eliminates the 504 risk entirely since the connection
  // stays open and turns arrive progressively.
  const startDebate = async () => {
    const msg = input.trim() || thread.findLast?.(m => m.role === "user")?.text || "What should we focus on today?";
    setBusy(true);
    setMode("debate");
    setThread(t => [...t, { role: "user", text: `🎭 DEBATE: ${msg}` }]);
    setInput("");
    const tk = localStorage.getItem("rawco_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/jarvis/swarm_debate_stream?message=${encodeURIComponent(msg)}&rounds=1&token=${encodeURIComponent(tk)}`;
    let evtSource;
    try {
      try { window.__rcoSwarmVoiceBusy = true; } catch {}
      evtSource = new EventSource(url);
      let voiceQueue = Promise.resolve();
      evtSource.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "turn") {
            setThread(prev => [...prev, {
              role: "agent", name: `${ev.agent} · R${ev.round}`, avatar: ev.avatar,
              voice_id: ev.voice_id, text: ev.reply, ok: true,
            }]);
            // Queue each turn's voice playback so they don't talk over each other
            if (ev.voice_id) {
              voiceQueue = voiceQueue.then(() => new Promise(res => {
                (async () => {
                  try {
                    const tts = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/tts`, {
                      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
                      body: JSON.stringify({ text: ev.reply, voice_id: ev.voice_id }),
                    });
                    if (tts.ok) {
                      const blob = await tts.blob();
                      const a = new Audio(URL.createObjectURL(blob));
                      a.onended = () => res();
                      a.onerror = () => res();
                      a.play().catch(() => res());
                    } else res();
                  } catch { res(); }
                  setTimeout(res, 12000);
                })();
              }));
            }
          } else if (ev.type === "plan" && ev.plan) {
            setThread(prev => [...prev, { role: "summary", text: `🎯 JARVIS PLAN:\n${ev.plan}` }]);
            voiceQueue = voiceQueue.then(() => new Promise(res => {
              (async () => {
                try {
                  const tts = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/tts`, {
                    method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
                    body: JSON.stringify({ text: ev.plan }),
                  });
                  if (tts.ok) {
                    const blob = await tts.blob();
                    const a = new Audio(URL.createObjectURL(blob));
                    a.onended = () => res();
                    a.onerror = () => res();
                    a.play().catch(() => res());
                  } else res();
                } catch { res(); }
                setTimeout(res, 15000);
              })();
            }));
          } else if (ev.type === "done") {
            // Wait for the trailing voice queue to fully drain before
            // releasing the global lock, otherwise useJarvis.speak() can
            // still cut off the last agent's final sentence.
            (async () => {
              try { await voiceQueue; } catch {}
              try { window.__rcoSwarmVoiceBusy = false; } catch {}
            })();
            evtSource.close();
            setBusy(false);
          } else if (ev.type === "error") {
            setThread(prev => [...prev, { role: "agent", name: "system", avatar: "!", text: `Debate error: ${ev.message}`, ok: false }]);
            evtSource.close();
            setBusy(false);
            try { window.__rcoSwarmVoiceBusy = false; } catch {}
          }
        } catch { /* malformed SSE chunk — skip */ }
      };
      evtSource.onerror = () => {
        evtSource.close();
        setBusy(false);
        try { window.__rcoSwarmVoiceBusy = false; } catch {}
      };
    } catch (e) {
      setThread(prev => [...prev, { role: "agent", name: "system", avatar: "!", text: `Debate failed to start: ${e.message}`, ok: false }]);
      setBusy(false);
    }
  };
  // Expose send to refs so the auto-message effect can trigger it
  sendRef.current = send;
  debateRef.current = startDebate;
  conclaveRef.current = startConclave;

  if (!open) return null;
  return (
    <div className="fixed z-[40] pointer-events-none"
         style={{ left: pos.x, top: pos.y, pointerEvents: "none" }}>
      <div className="rc-panel pointer-events-auto flex flex-col w-[360px] max-h-[60vh] shadow-2xl"
        data-testid="swarm-chat-panel"
        style={{ background: "rgba(0, 4, 12, 0.96)", border: "1px solid var(--rc-gold)" }}>
        <div
          className="flex justify-between items-center p-3 border-b border-[var(--rc-gold-dim)] cursor-grab active:cursor-grabbing select-none"
          onMouseDown={onDragStart}
          data-testid="swarm-chat-drag-handle"
          title="Drag to move"
        >
          <div>
            <div className="font-display rc-glow-text text-sm">// SWARM CHAT</div>
            <div className="text-[10px] opacity-60" data-testid="swarm-chat-subtitle">
              {mode === "conclave"
                ? "🤝 HUDDLE mode — agents huddle sequentially, calling each other by name"
                : mode === "debate"
                ? "🎭 DEBATE mode — agents round-robin then Jarvis synthesises the plan"
                : "Every agent answers in parallel · synced across devices"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearHistory}
              disabled={busy || thread.length === 0}
              title="Clear the entire conversation history (synced)"
              data-testid="swarm-chat-clear"
              className="text-[9px] tracking-widest border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] text-[var(--rc-text-dim)] hover:text-[var(--rc-gold)] px-2 py-1 disabled:opacity-30"
            >CLEAR</button>
            <button onClick={onClose} className="text-[var(--rc-gold)] hover:opacity-70" data-testid="swarm-chat-close">✕</button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 text-xs" style={{ minHeight: 200 }}>
          {thread.length === 0 && (
            <div className="text-[11px] opacity-60 text-center py-6">
              Type any issue or question — Tom, Sarah, Eva, Scout and Dobby will all chime in.
              <div className="mt-3 space-y-1.5">
                {["my dashboard shows 1033 fake issues",
                  "how do I get more customers on snapdog?",
                  "what should I post on instagram today?",
                  "why is my morning brief not pulling clothing app stats?"].map((s) => (
                    <button key={s} onClick={() => setInput(s)}
                      className="block w-full text-left text-[10px] border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] px-2 py-1 opacity-80 hover:opacity-100"
                      data-testid={`swarm-suggest-${s.slice(0,10)}`}>
                      &quot;{s}&quot;
                    </button>
                  ))}
              </div>
            </div>
          )}
          {thread.map((m, i) => (
            <div key={i} data-testid={`swarm-msg-${m.role}-${i}`}
              className={m.role === "user" ? "text-right" : m.role === "action" ? "" : "flex gap-2"}>
              {m.role === "agent" && (
                <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                  style={{ background: m.ok === false ? "#7a1f1f" : "var(--rc-gold-dim)", color: "var(--rc-bg)" }}>
                  {m.avatar || m.name?.[0] || "?"}
                </div>
              )}
              {m.role === "user" && (
                <div className="inline-block bg-[var(--rc-gold-dim)] text-[var(--rc-bg)] px-2 py-1 rounded max-w-[85%] text-[11px] whitespace-pre-wrap">{m.text}</div>
              )}
              {m.role === "agent" && (
                <div className="flex-1 max-w-[88%]">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="rc-label text-[9px] opacity-70">{m.name?.toUpperCase()}</div>
                    {m.voice_id && (
                      <button
                        onClick={() => speakAgent(m.text, m.voice_id, m.name)}
                        title={`Hear ${m.name} say this`}
                        data-testid={`swarm-speak-${m.name?.toLowerCase()}`}
                        className="text-[10px] opacity-50 hover:opacity-100 hover:rc-glow-text transition-all px-1"
                      >🔊</button>
                    )}
                  </div>
                  <div className="text-[11px] whitespace-pre-wrap opacity-90">{m.text}</div>
                </div>
              )}
              {m.role === "summary" && (
                <div className="border border-[var(--rc-gold)] p-3 bg-[var(--rc-gold-dim)] bg-opacity-15"
                     data-testid="swarm-summary-card">
                  <div className="rc-label text-[10px] opacity-80 mb-1">🍒 JARVIS VERDICT (spoken)</div>
                  <div className="text-[12px] rc-glow-text italic">&quot;{m.text}&quot;</div>
                </div>
              )}
              {m.role === "action" && (
                <div className="border border-[var(--rc-gold)] p-3 bg-[var(--rc-gold-dim)] bg-opacity-10"
                     data-testid="swarm-action-card">
                  <div className="rc-label text-[10px] opacity-80 mb-1">PROPOSED FIX</div>
                  <div className="text-[12px] font-display rc-glow-text mb-2">{m.action.label}</div>
                  <div className="text-[10px] opacity-60 mb-3">{m.action.delegate_to} will handle this for you.</div>
                  {!m.action.executed && (
                    <div className="flex gap-2">
                      <button
                        data-testid="swarm-action-yes"
                        onClick={() => runAction(m.action, i)}
                        disabled={busy}
                        className="rc-btn !text-[10px] !py-1.5 flex-1"
                      >✅ YES, FIX IT</button>
                      <button
                        data-testid="swarm-action-no"
                        onClick={() => setThread(t => t.map((mm, ii) => ii === i ? {
                          ...mm, action: { ...mm.action, executed: true, dismissed: true, result_message: "Dismissed." }
                        } : mm))}
                        disabled={busy}
                        className="border border-[var(--rc-gold-dim)] text-[var(--rc-text-dim)] px-3 py-1.5 text-[10px] tracking-widest hover:opacity-100 opacity-70"
                      >NO THANKS</button>
                    </div>
                  )}
                  {m.action.executed && (
                    <div className={`text-[11px] mt-1 ${m.action.failed ? "text-red-400" : m.action.dismissed ? "opacity-60" : "rc-glow-text"}`}
                         data-testid="swarm-action-result">
                      {m.action.failed ? "❌ " : m.action.dismissed ? "× " : "✓ "}{m.action.result_message}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="text-[10px] opacity-60 italic" data-testid="swarm-busy">// All agents thinking…</div>
          )}
        </div>

        <div className="border-t border-[var(--rc-gold-dim)] p-2 flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Ask all my agents at once…"
            className="flex-1 bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1.5 text-xs"
            data-testid="swarm-chat-input"
            disabled={busy}
          />
          <button onClick={send} disabled={busy || !input.trim()} className="rc-btn !text-[10px] !py-1.5 !px-3"
            data-testid="swarm-chat-send">{busy ? "…" : "ASK ALL"}</button>
          <button onClick={startDebate} disabled={busy} title="Agents debate each other, then Jarvis gives the plan"
            className="border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] text-[var(--rc-gold)] !text-[10px] tracking-widest !py-1.5 !px-2"
            data-testid="swarm-chat-debate">🎭 DEBATE</button>
          <button onClick={startConclave} disabled={busy} title="Agents huddle sequentially — each references the others by name and builds toward a joint plan"
            className="border border-[color:rgba(217,70,239,0.5)] hover:border-[color:rgb(217,70,239)] text-[color:rgb(240,171,252)] !text-[10px] tracking-widest !py-1.5 !px-2"
            data-testid="swarm-chat-conclave">🤝 HUDDLE</button>
        </div>
      </div>
    </div>
  );
}
